import React from 'react';
import { gaPageView } from '../helpers/Analytics';
import {
  FORMELEMENTS_DATEPICKER,
  FORMELEMENTS_DROPDOWN,
  FORMELEMENTS_TEXTAREA,
  FORMELEMENTS_TIMEPICKER
} from '../FormBuilder/FormElements';
import FormGenerator from '../FormBuilder/FormGenerator';
import { formWidgetHtmlId } from '../FormBuilder/utils';
import PropTypes from 'prop-types';
import {
  checkIfFormDoesNotNeedConfirm,
  isFormItemNotEditable,
  itemUsingKeyboard,
  queryByFormItemId
} from '../helpers/formUtils';
import { f7navigator } from '../helpers/navigator';
import { getViewPortHeight } from '../helpers/utils';
import II18 from '../translate/II18';
import withForms from '../formsCommon/withForms';
import { getExternalApiFormActionTitleKey } from '../helpers/externalApiValidation';

export default function withQuickForm(WrappedComponent) {
  // noinspection JSPotentiallyInvalidUsageOfThis
  class WithQuickFormWrapper extends React.Component {
    constructor(props) {
      super(props);

      this.state = {
        showErrorsOnCurrentPage: false,
        currentPage:
          (props.startFromLastPage || props.forceBackwardAnimation) &&
          !props.smartFillFormMode
            ? this.findLastPageWhichIsDone()
            : 0,
        initialHeight: getViewPortHeight()
      };
    }

    componentDidMount() {
      gaPageView('QuickForm');

      document.addEventListener('keydown', this.handleEnterAsTab);
      this.pushDocumentStateUp();
    }

    componentWillUnmount() {
      document.removeEventListener('keydown', this.handleEnterAsTab);
    }

    componentDidUpdate(prevProps, prevState) {
      const maxPage = Math.max(0, (this.props.formStructure?.length || 1) - 1);
      if (this.state.currentPage > maxPage) {
        this.setCurrentPage(maxPage);
        return;
      }

      if (this.props.moveToFirstError && !prevProps.moveToFirstError) {
        this.moveToFirstRequiredElement();
      }

      if (
        this.props.lastSelectedItem &&
        !this.checkIfLastItemStillPresentOnPage(this.props.lastSelectedItem)
      ) {
        this.props.setLastSelectedItem(null);
      }

      if (
        prevProps.pagesValid[this.state.currentPage] !==
        this.props.pagesValid[this.state.currentPage]
      ) {
        this.pushDocumentStateUp();
      }

      if (prevState.currentPage !== this.state.currentPage) {
        this.setState({
          showErrorsOnCurrentPage: false
        });
      }
    }

    checkIfLastItemStillPresentOnPage(item) {
      const page = this.props.formStructure?.[item.pageIndex];
      return (
        this.getCurrentPage() !== item.pageIndex ||
        (page?.[item.elementIndex] && item.id === page[item.elementIndex].id)
      );
    }

    findFormElementFromEventTarget = target => {
      if (!target) return null;

      const page = this.getCurrentPageItems();

      for (const element of page) {
        const id = formWidgetHtmlId(element);
        if (
          target.id === id ||
          target.getAttribute?.('name') === id ||
          target.closest?.('[id="' + id + '"]')
        ) {
          return element;
        }
      }

      return null;
    };

    handleEnterAsTab = event => {
      if (event.keyCode !== 13 && event.key !== 'Enter') return;
      if (event.isComposing || event.keyCode === 229) return;
      if (event.target?.tagName === 'TEXTAREA') return;
      if (!event.target?.closest?.('.quickForm')) return;

      let element = this.findFormElementFromEventTarget(event.target);

      // Fallback for radios/checkboxes/selects where focus is inside the field
      // wrapper but not on the widget root id (avoid stealing Enter from nav buttons).
      if (
        !element &&
        event.target.closest?.('.inputElement') &&
        this.props.lastSelectedItem?.element !== FORMELEMENTS_TEXTAREA
      ) {
        element = this.props.lastSelectedItem;
      }

      if (!element) return;

      event.preventDefault();
      this.goNextElement(element);
    };

    stopBodyScrolling(bool) {
      if (bool === true) {
        document.body.classList.add('disableScroll');
      } else {
        document.body.classList.remove('disableScroll');
      }
    }

    moveToFirstElement = () => {
      const first = this.getCurrentPageItems().find(
        item => !isFormItemNotEditable(item)
      );
      if (first) {
        this.focusInputElement(first, true, true);
      }
    };

    findLastPageWhichIsDone = () => {
      const err = this.props.getFirstErrorElement();
      return err ? err.element.pageIndex : this.props.formStructure.length - 1;
    };

    moveToFirstRequiredElement = () => {
      const err = this.props.getFirstErrorElement();

      this.props.cancelMoveToFirstError();

      if (err) {
        this.focusInputElement(err.element);
      }
    };

    goNextElement = currentElement => {
      const { pageIndex, elementIndex } = currentElement;
      this.props.goNextElement(pageIndex, elementIndex, item => {
        item && this.focusInputElement(item);
      });
    };

    focusInputElement = (item, noScroll, justSetFocus) => {
      const input = document.getElementById(formWidgetHtmlId(item));

      if (!input) return;

      let needFocus = itemUsingKeyboard(item.element);

      if (document.activeElement) {
        document.activeElement.blur();
      }

      if (needFocus) {
        if (!noScroll) this.scrollToItem(item);
        input.focus({
          preventScroll: true
        });
      } else if (!justSetFocus) {
        const thiss = this;
        setTimeout(function() {
          if (!noScroll) thiss.scrollToItem(item);
          input.focus({
            preventScroll: true
          });
          if (
            item.element === FORMELEMENTS_DATEPICKER ||
            item.element === FORMELEMENTS_TIMEPICKER
          ) {
            input.click();
          }
          if (item.element === FORMELEMENTS_DROPDOWN) {
            const dropDownInput = queryByFormItemId(
              formWidgetHtmlId(item),
              ' input'
            );
            dropDownInput && dropDownInput.focus();
          }
        }, 300);
      }

      this.props.setLastSelectedItem(item);
    };

    scrollToItem = item => {
      const input = document.getElementById(formWidgetHtmlId(item));

      if (window.isDesktop || this.$f7.device.ios) {
        input?.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      } else {
        const container = document.querySelector(
          '.quickForm.page-current .page-content'
        );

        const scrollGap = this.state.initialHeight - getViewPortHeight();

        container.scrollBy({
          top:
            input.getBoundingClientRect().top -
            100 -
            (this.$f7.device.ios ? 0 : scrollGap),
          left: 0
          //behavior: 'smooth'
        });
      }
    };

    generateForm = (desktop, page, readOnly) => {
      const {
        scanEnded,
        formErrors,
        formResult,
        formStructure,
        showRequiredErrors
      } = this.props;

      return (
        <FormGenerator
          documentName={this.props.document.name}
          documentId={this.props.document.id}
          activeElementIndex={this.props.lastSelectedItem?.elementIndex}
          key={page ? page : 0}
          desktop={desktop}
          allowDelayElements
          quickForm={true}
          ios={this.$f7.device.ios}
          f7={this.$f7}
          readOnly={scanEnded || readOnly}
          formResult={formResult}
          formStructure={formStructure[page ? page : 0]}
          formErrors={formErrors}
          requestedDocuments={this.props.requestedDocuments}
          showRequiredErrors={
            showRequiredErrors || this.state.showErrorsOnCurrentPage
          }
          onAnyChange={this.props.updateFormResult}
          onItemClick={this.props.onFormItemClicked}
          onItemFocus={this.props.onFormItemFocused}
        />
      );
    };

    onDone = () => {
      const skippingConfirm = checkIfFormDoesNotNeedConfirm(
        this.props.formStructure,
        this.props.formResult
      );

      this.props.onFormDone(skippingConfirm === true);
    };

    continueToPdf = () => {
      setTimeout(() => {
        f7navigator.toPDFForm(
          this.props.document.id,
          {
            currentPage: 0, //this.getCurrentPage(),
            returnFromQuickForm: true
          },
          {
            clearPreviousHistory: true
          }
        );
      }, 300);
    };

    onSaveButtonClick = () => {
      setTimeout(() => {
        if (this.props.smartFillFormMode) {
          this.props.navigateBack();
        } else
          this.props.formValid || this.props.scanEnded
            ? this.onDone()
            : this.props.checkDataAndTryBack();
      }, 100);
    };

    getCurrentPageTitle = () =>
      this.props.smartFillFormMode
        ? this.props.document.smartFillStepsTitle?.[this.getCurrentPage()] ||
          II18.get('title_step')
        : this.props.formStructure.length > 1
        ? II18.get('common_page_x', this.getCurrentPage() + 1)
        : '';

    getNextBtnTitle = () =>
      II18.get(
        this.props.smartFillFormMode
          ? 'title_next'
          : this.getCustomNextBtnTitle() ||
              (this.isLastPage() &&
              (this.props.formValid || this.props.scanEnded)
                ? !this.props.document?.rejected && this.props.scanEnded
                  ? 'title_close'
                  : getExternalApiFormActionTitleKey(
                      this.props.document,
                      this.props.formStructure,
                      this.props.formResult,
                      'title_done'
                    )
                : 'title_next')
      );

    getBackBtnTitle = () =>
      this.getCustomBackBtnTitle() || II18.get('title_back');

    getCustomBackBtnTitle = () =>
      this.getCurrentPage() > 0 &&
      this.getCurrentPageItems().find(item => item.element === 'PrevButton')
        ?.label;

    getCustomNextBtnTitle = () =>
      this.getCurrentPageItems().find(item => item.element === 'NextButton')
        ?.label;

    setCurrentPage = page =>
      this.setState(
        {
          currentPage: page
        },
        this.pushDocumentStateUp
      );

    pushDocumentStateUp = () => {
      this.props.shareActiveDocumentState({
        currentPageIsValid: this.props.pagesValid[this.getCurrentPage()],
        currentPageIsLast: this.isLastPage(),
        currentPageIndex: this.getCurrentPage(),
        currentPageTitle: this.getCurrentPageTitle(),
        customPrevBtnTitle: this.getCustomBackBtnTitle(),
        customNextBtnTitle: this.getCustomNextBtnTitle()
      });
    };

    getCurrentPageItems = () =>
      this.props.formStructure?.[this.getCurrentPage()] || [];

    getCurrentPage = () => {
      const maxPage = Math.max(0, (this.props.formStructure?.length || 1) - 1);
      return Math.min(Math.max(0, this.state.currentPage), maxPage);
    };

    goPrevStep = () =>
      this.getCurrentPage() > 0 &&
      this.setCurrentPage(this.getCurrentPage() - 1);

    goNextStep = () =>
      this.getCurrentPage() < this.props.formStructure.length - 1 &&
      this.setCurrentPage(this.getCurrentPage() + 1);

    actionOnLastPage = () => {
      if (
        this.props.smartFillFormMode &&
        (this.props.formValid || !this.props.validateSmartFill)
      ) {
        this.continueToPdf();
      } else {
        if (this.props.formValid || this.props.scanEnded) {
          this.onSaveButtonClick();
        } else {
          this.goNextStep();
        }
      }
    };

    isLastPage = () =>
      this.getCurrentPage() === this.props.formStructure.length - 1;

    actionNextStep = () => {
      const currentPageIsValid =
        this.props.scanEnded ||
        this.props.pagesValid[this.getCurrentPage()] ||
        (this.props.smartFillFormMode && !this.props.validateSmartFill);

      if (currentPageIsValid) {
        if (this.isLastPage()) {
          this.actionOnLastPage();
        } else {
          this.goNextStep();
        }
      } else {
        const requiredElement = this.props.getFirstErrorElement();
        if (
          this.props.lastSelectedItem &&
          requiredElement?.element?.id === this.props.lastSelectedItem.id
        ) {
          this.setState({
            showErrorsOnCurrentPage: true
          });
        }
        this.moveToFirstRequiredElement();
      }
    };

    render() {
      return (
        <WrappedComponent
          validateSmartFill={this.props.validateSmartFill}
          backBtnTitle={this.getBackBtnTitle()}
          nextBtnTitle={this.getNextBtnTitle()}
          customBackBtnTitle={this.getCustomBackBtnTitle()}
          customNextBtnTitle={this.getCustomNextBtnTitle()}
          pageTitle={this.getCurrentPageTitle()}
          currentPage={this.getCurrentPage()}
          currentPageIsValid={this.props.pagesValid[this.getCurrentPage()]}
          currentPageIsLast={this.isLastPage()}
          onDone={this.onDone}
          goPrevStep={this.goPrevStep}
          goNextStep={this.actionNextStep}
          onSaveButtonClick={this.onSaveButtonClick}
          continueToPdf={this.continueToPdf}
          focusInputElement={this.focusInputElement}
          moveToFirstRequiredElement={this.moveToFirstRequiredElement}
          moveToFirstElement={this.moveToFirstElement}
          generateForm={this.generateForm}
          {...this.props}
        />
      );
    }
  }

  WithQuickFormWrapper.propTypes = {
    forceBackwardAnimation: PropTypes.bool,
    startFromLastPage: PropTypes.bool,
    smartFillFormMode: PropTypes.bool,
    onSaveButtonClick: PropTypes.func,
    navigateBack: PropTypes.func,
    continueToPdf: PropTypes.func,
    onFormDone: PropTypes.func,
    cancelMoveToFirstError: PropTypes.func,
    moveToFirstError: PropTypes.bool,
    showRequiredErrors: PropTypes.bool,
    formValid: PropTypes.bool,
    formActionTitleKey: PropTypes.string,
    pagesValid: PropTypes.array,
    scanEnded: PropTypes.bool,
    tryBack: PropTypes.func,
    goNextElement: PropTypes.func,
    getFirstErrorElement: PropTypes.func,
    checkDataAndTryBack: PropTypes.func,
    updateFormResult: PropTypes.func,
    generateForm: PropTypes.func,
    document: PropTypes.object,
    formErrors: PropTypes.object,
    formResult: PropTypes.object,
    formStructure: PropTypes.arrayOf(PropTypes.array),
    cachedSignature: PropTypes.any,
    shareActiveDocumentState: PropTypes.func,
    validateSmartFill: PropTypes.bool,
    collectBioDataConfirmed: PropTypes.bool,
    onCollectBioDataConfirmed: PropTypes.func,
    onFormItemClicked: PropTypes.func,
    onFormItemFocused: PropTypes.func,
    setLastSelectedItem: PropTypes.func,
    lastSelectedItem: PropTypes.object,
    requestedDocuments: PropTypes.array
  };

  return withForms(WithQuickFormWrapper);
}
